import { useRef } from "react";

type NumericInputProps = {
  label: string;
  shortLabel?: string;
  unit: string;
  value: number;
  textValue: string;
  decimals: number;
  locale: string;
  disabled?: boolean;
  onChange: (value: string) => void;
};

const easterEggTriggers = ["martin", "scheisswillinger"];

function decimalSeparator(locale: string) {
  return locale.startsWith("de") ? "," : ".";
}

function finalizeInputText(value: string, locale: string) {
  const separator = decimalSeparator(locale);
  const normalizedSeparator = value.replace(/[,.]/g, separator);
  const withoutTrailingSeparator = normalizedSeparator.endsWith(separator)
    ? normalizedSeparator.slice(0, -separator.length)
    : normalizedSeparator;
  const [whole, fraction] = withoutTrailingSeparator.split(separator);

  if (fraction === undefined) return withoutTrailingSeparator;

  const trimmedFraction = fraction.replace(/0+$/, "");
  return trimmedFraction ? `${whole}${separator}${trimmedFraction}` : whole;
}

function dispatchMartinEasterEgg() {
  window.dispatchEvent(new Event("martin-easter-egg"));
}

function normalizeEasterEggText(value: string) {
  return value
    .toLocaleLowerCase()
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z]/g, "");
}

export function NumericInput({
  label,
  shortLabel,
  unit,
  value,
  textValue,
  decimals,
  locale,
  disabled,
  onChange
}: NumericInputProps) {
  const easterEggBuffer = useRef("");
  const formattedValue = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  }).format(Number.isFinite(value) ? value : 0);

  function rememberEasterEggText(value: string) {
    const letters = normalizeEasterEggText(value);
    if (!letters) return;

    easterEggBuffer.current = `${easterEggBuffer.current}${letters}`.slice(-48);
    if (easterEggTriggers.some((trigger) => easterEggBuffer.current.includes(trigger))) {
      easterEggBuffer.current = "";
      dispatchMartinEasterEgg();
    }
  }

  return (
    <label className={disabled ? "input-field is-result" : "input-field"}>
      <span>
        <span className="label-full">{label}</span>
        <span className="label-short">{shortLabel ?? label}</span>
      </span>
      <div className="input-shell">
        {disabled ? (
          <output>{formattedValue}</output>
        ) : (
          <input
            type="text"
            inputMode="decimal"
            pattern="[0-9]*[.,]?[0-9]*"
            autoComplete="off"
            spellCheck={false}
            value={textValue}
            onChange={(event) => {
              rememberEasterEggText(event.target.value);
              onChange(event.target.value);
            }}
            onBlur={(event) => onChange(finalizeInputText(event.target.value, locale))}
          />
        )}
        <b>{unit}</b>
      </div>
    </label>
  );
}
